import { createInfiniteQuery } from '@funtech-int/rival';
import { SportShowcase, sportShowcaseQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useInfinitePagination, useResources } from '@funtech-int/rival-react';
import { useFeatureFlags } from '@funtech-int/yango-shared';

import { EmptySportShowcaseError } from '@/shared/lib/errors/EmptySportShowcaseError';

import { GraphqlClient } from '../lib/graphqlClient';

import { useGraphqlClient } from './useGraphqlClient';

const SPORT_SHOWCASE_LIMIT = 10;
const SPORT_SHOWCASE_OFFSET = 0;

interface SportShowcaseParams {
    id: string;
}

interface SportShowcaseQueryContext {
    params: SportShowcaseParams;
    client: GraphqlClient;
}

interface SportShowcasePageInfo {
    offset: number;
}

const sportShowcaseResourceQuery = createInfiniteQuery<
    SportShowcaseQueryContext,
    SportShowcase,
    SportShowcasePageInfo
>({
    key(ctx) {
        return [sportShowcaseQuery.name, ctx.params.id].join(':');
    },
    fetch(ctx) {
        return ctx.client
            .fetch({
                operation: sportShowcaseQuery,
                variables: {
                    showcaseId: ctx.params.id,
                    limit: SPORT_SHOWCASE_LIMIT,
                    offset: SPORT_SHOWCASE_OFFSET,
                },
            })
            .then(({ data }) => {
                if (!data || !data.selections.length) {
                    throw new EmptySportShowcaseError(ctx.params.id);
                }

                return data;
            });
    },
    nextPageInfo(data) {
        if (data?.hasMore) {
            return {
                offset: data.offset + SPORT_SHOWCASE_LIMIT,
            };
        }
    },
    toJSON(data) {
        return data;
    },
    hydrate(data: any) {
        return sportShowcaseQuery.hydrate(data) as any;
    },
});

interface UseSportShowcaseQueryParams {
    id: string;
}

export function useSportShowcaseQuery({ id }: UseSportShowcaseQueryParams) {
    const { flags } = useFeatureFlags();
    const client = useGraphqlClient();
    const pagination = useInfinitePagination();

    const showcaseRefetchTime = flags.get('showcaseRefetchTime', 'number', 3600);

    const [sportShowcase] = useResources(
        sportShowcaseResourceQuery(
            {
                client,
                params: {
                    id,
                },
            },
            { pagination, lifetime: showcaseRefetchTime * 1000 }
        )
    );

    return { sportShowcase, pagination };
}
